import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { useWorkspace } from './WorkspaceContext';
import * as productService from '../services/productService';
import * as templateService from '../services/templateService';
import type {
  Product,
  Category,
  CustomizationField,
  ProductVariation,
  VariationImage,
  ProductTemplate,
  TemplateShape,
  ShapeProcessSettings,
} from '@jigflow/shared';

interface ProductContextValue {
  products: Product[];
  categories: Category[];
  loading: boolean;
  error: string | null;
  // Read operations
  getFieldsByProductId(productId: string): Promise<CustomizationField[]>;
  getVariationsByProductId(productId: string): Promise<ProductVariation[]>;
  getVariationImages(variationId: string): Promise<VariationImage[]>;
  getTemplateByProductId(productId: string, variationId?: string, machineId?: string): Promise<ProductTemplate | null>;
  getShapesByTemplateId(templateId: string): Promise<TemplateShape[]>;
  getProcessSettingsByShapeId(shapeId: string): Promise<ShapeProcessSettings[]>;
  // CRUD operations
  addProduct(data: Partial<Product>): Promise<Product>;
  updateProduct(id: string, data: Partial<Product>): Promise<void>;
  deleteProduct(id: string): Promise<void>;
  addCategory(name: string): Promise<void>;
  updateCategory(id: string, name: string): Promise<void>;
  deleteCategory(id: string): Promise<void>;
  // Field CRUD operations
  addField(field: Omit<CustomizationField, 'id'>): Promise<CustomizationField>;
  updateField(field: CustomizationField): Promise<void>;
  deleteField(fieldId: string): Promise<void>;
  reorderFields(productId: string, fieldIds: string[]): Promise<void>;
  refreshProducts(): Promise<void>;
}

const ProductContext = createContext<ProductContextValue | null>(null);

export function ProductProvider({ children }: { children: React.ReactNode }) {
  const { workspace } = useWorkspace();
  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const workspaceId = workspace?.id;

  const fetchData = useCallback(async () => {
    if (!workspaceId) return;
    try {
      setLoading(true);
      setError(null);
      const [prods, cats] = await Promise.all([
        productService.getProducts(workspaceId),
        productService.getCategories(workspaceId),
      ]);
      setProducts(prods);
      setCategories(cats);
    } catch (err) {
      console.error('[Product] Failed to fetch:', err);
      setError(err instanceof Error ? err.message : 'Failed to load products');
    } finally {
      setLoading(false);
    }
  }, [workspaceId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const getFieldsByProductId = useCallback(
    (productId: string) => productService.getFieldsByProductId(productId),
    []
  );

  const getVariationsByProductId = useCallback(
    (productId: string) => productService.getVariationsByProductId(productId),
    []
  );

  const getVariationImages = useCallback(
    (variationId: string) => productService.getVariationImages(variationId),
    []
  );

  const getTemplateByProductId = useCallback(
    (productId: string, variationId?: string, machineId?: string) =>
      templateService.getTemplateByProductId(productId, variationId, machineId),
    []
  );

  const getShapesByTemplateId = useCallback(
    (templateId: string) => templateService.getShapesByTemplateId(templateId),
    []
  );

  const getProcessSettingsByShapeId = useCallback(
    (shapeId: string) => templateService.getProcessSettingsByShapeId(shapeId),
    []
  );

  const addProduct = useCallback(
    async (data: Partial<Product>) => {
      if (!workspaceId) throw new Error('No workspace');
      const created = await productService.createProduct(workspaceId, data);
      setProducts((prev) => [...prev, created]);
      return created;
    },
    [workspaceId]
  );

  const updateProduct = useCallback(
    async (id: string, data: Partial<Product>) => {
      const updated = await productService.updateProduct(id, data);
      setProducts((prev) => prev.map((p) => (p.id === id ? updated : p)));
    },
    []
  );

  const deleteProduct = useCallback(
    async (id: string) => {
      await productService.deleteProduct(id);
      setProducts((prev) => prev.filter((p) => p.id !== id));
    },
    []
  );

  const addCategory = useCallback(
    async (name: string) => {
      if (!workspaceId) throw new Error('No workspace');
      // Use supabase directly for category creation since service doesn't expose it
      const { supabase } = await import('../services/supabase');
      const { data, error: err } = await supabase
        .from('categories')
        .insert({ workspace_id: workspaceId, name })
        .select()
        .single();
      if (err) throw err;
      setCategories((prev) => [...prev, { id: data.id, name: data.name }]);
    },
    [workspaceId]
  );

  const updateCategory = useCallback(
    async (id: string, name: string) => {
      const { supabase } = await import('../services/supabase');
      const { error: err } = await supabase
        .from('categories')
        .update({ name })
        .eq('id', id);
      if (err) throw err;
      setCategories((prev) => prev.map((c) => (c.id === id ? { ...c, name } : c)));
    },
    []
  );

  const deleteCategory = useCallback(
    async (id: string) => {
      const { supabase } = await import('../services/supabase');
      const { error: err } = await supabase
        .from('categories')
        .delete()
        .eq('id', id);
      if (err) throw err;
      setCategories((prev) => prev.filter((c) => c.id !== id));
    },
    []
  );

  const addField = useCallback(
    (field: Omit<CustomizationField, 'id'>) => {
      if (!workspaceId) throw new Error('No workspace');
      return productService.addField(workspaceId, field);
    },
    [workspaceId]
  );

  const updateFieldCb = useCallback(
    (field: CustomizationField) => productService.updateField(field),
    []
  );

  const deleteFieldCb = useCallback(
    (fieldId: string) => productService.deleteField(fieldId),
    []
  );

  const reorderFields = useCallback(
    (productId: string, fieldIds: string[]) => productService.reorderFields(productId, fieldIds),
    []
  );

  return (
    <ProductContext.Provider
      value={{
        products,
        categories,
        loading,
        error,
        getFieldsByProductId,
        getVariationsByProductId,
        getVariationImages,
        getTemplateByProductId,
        getShapesByTemplateId,
        getProcessSettingsByShapeId,
        addProduct,
        updateProduct,
        deleteProduct,
        addCategory,
        updateCategory,
        deleteCategory,
        addField,
        updateField: updateFieldCb,
        deleteField: deleteFieldCb,
        reorderFields,
        refreshProducts: fetchData,
      }}
    >
      {children}
    </ProductContext.Provider>
  );
}

export function useProducts() {
  const context = useContext(ProductContext);
  if (!context) {
    throw new Error('useProducts must be used within a ProductProvider');
  }
  return context;
}
